import { LocalStorageKey } from '@/enums/LocalStorageKey'
import DashboardView from '@/views/DashboardView.vue'
import PeopleView from '@/views/PeopleView.vue'
import PreviewView from '@/views/PreviewView.vue'
import SchedulerView from '@/views/SchedulerView.vue'
import SelfScheduleView from '@/views/SelfSchedulerView.vue'
import SettingsView from '@/views/SettingsView.vue'
import { IFusionAuth, getAuthInstance, getPermissionsInstance } from '@fusion/auth-v3'
import axios from 'axios'
import { type App, type Component } from 'vue'
import {
  RouteLocationNormalizedGeneric,
  createRouter,
  createWebHistory,
  type RouteRecordRaw,
  type Router
} from 'vue-router'

let _isReady = false
let _router: Router

/**
 * Re-authenticates the user by attempting to get a new token.
 * @param to The route being navigated to.
 * @param auth The Auth0 instance to get the token from.
 * @param useCache Whether to use the cache for the token retrieval. Defaults to true.
 */
const _reauthenticate = async (
  to: RouteLocationNormalizedGeneric,
  auth: ReturnType<typeof getAuthInstance>,
  useCache: boolean = true
) => {
  try {
    await auth.getTokenSilently({ cacheMode: useCache ? 'on' : 'off' })
  } catch (error: any) {
    console.warn('Silent token refresh failed, force login', error.message)
    await auth.loginWithRedirect({
      appState: { target: to.fullPath }
    })
  }
}

/**
 * Register all navigation components required to properly render the router.
 * @param app The Vue application instance.
 * @returns
 */
function _registerComponents(app: App): Record<string, Component<any>> {
  app.component('DashboardView', DashboardView)
  app.component('SettingsView', SettingsView)
  app.component('PeopleView', PeopleView)
  app.component('SchedulerView', SchedulerView)
  app.component('SelfScheduleView', SelfScheduleView)
  app.component('PreviewView', PreviewView)
  return app._context.components
}

/**
 * Waits for the Auth0 instance to be ready or until the maxWait time is reached.
 * @param auth The Auth0 instance to check if the user is authenticated.
 * @param maxWait
 * @returns A promise that resolves to true if the Auth0 instance is ready, false otherwise.
 */
const _waitUntilReady = async (auth: IFusionAuth, maxWait = 20000) => {
  const start = Date.now()
  while (auth.loading && Date.now() - start < maxWait) {
    await new Promise((res) => setTimeout(res, 500))
  }
  return !auth.loading
}

/**
 * Gets the router dynamically using data from the database.
 * @param app The Vue application instance.
 * @param auth0 The Auth0 plugin instance.
 * @returns
 */
const _initializeRouter = async (app: App<Element>): Promise<Router> => {
  const applicationId = APP_ENV.VITE_APP_APPLICATION_ID
  let response

  // Cache the navigation, If it ever fails pull from the cache.
  try {
    response = await axios.get(
      `${APP_ENV.VITE_APP_APPLICATIONS_HOST}/applications/${applicationId}/navigation`
    )
    localStorage.setItem(LocalStorageKey.Navigation, JSON.stringify(response.data))
  } catch {
    const data = localStorage.getItem(LocalStorageKey.Navigation)
    if (data) {
      response = { status: 200, data: JSON.parse(data) }
    } else {
      response = { status: 500 }
    }
  }

  const components = _registerComponents(app)
  const routes: Array<RouteRecordRaw> = []

  if (response.status >= 200 && response.status <= 299 && Array.isArray(response.data)) {
    response.data.forEach((route: RouteRecordRaw) => {
      if (route.meta) {
        const component: Component = components[route.meta.componentName]
        if (component || route.path === '*') {
          route.component = component
          route.meta.routePermissions.alwaysVisible = true
          routes.push(route)
        } else {
          console.info(`Router - missing component name in meta field: ${route.meta.componentName}`)
        }
      } else {
        console.info(`Router - missing component name, route was skipped.`)
      }
    })
  } else {
    console.info('Failed to load navigation routes: ', response.statusText)
  }

  _router = createRouter({
    history: createWebHistory(APP_ENV.VITE_APP_PUBLIC_PATH),
    routes
  })

  _router.beforeEach(async (to, from, next) => {
    const auth = getAuthInstance()

    // If the Auth0 instance is not ready, wait for it to be ready or until the max wait time is reached.
    if (!_isReady) {
      _isReady = await _waitUntilReady(auth, 20000)
      if (!_isReady) {
        await _reauthenticate(to, auth, false)
        return
      }
    }

    // Check if the the state is invalid, if yes then re-authenticate.
    if (auth.error?.message?.includes('Invalid state')) {
      await auth.loginWithRedirect({ appState: { target: to.fullPath } })
      return
    }

    const slugs = to.meta.routePermissions?.slugs ?? []
    const authRequired = to.meta?.routePermissions?.authRequired

    if (auth.isAuthenticated) {
      try {
        await auth.getTokenSilently({ cacheMode: 'on' })
      } catch {
        // Token expired or missing, re-authenticate
        await _reauthenticate(to, auth, false)
        return
      }

      // User is authenticated, check permissions
      const permissionsManager = getPermissionsInstance()
      const isAllowed = permissionsManager.isAllowed(slugs)
      if (isAllowed) {
        // User has required permissions')
        next()
      } else {
        // User does not have required permissions, redirecting to home page
        next('/')
      }
    } else if (authRequired) {
      // User is not authenticated and auth is required, re-authenticate
      await _reauthenticate(to, auth)
      return
    } else {
      // Auth not required, proceeding to route
      next()
    }
  })
  return _router
}

/**
 * Ensure the router is created only once.
 * @param app
 * @returns
 */
export const getRouter = async (app: App<Element>): Promise<Router> => {
  return _initializeRouter(app)
}
